import { Controller } from "@hotwired/stimulus"
import { DebounceHelper } from "~/helpers/debounce_helper"
import { AttachmentEventHelper } from "~/helpers/attachment_event_helper"

export default class extends Controller {
  static get targets() {
    return ["bodyField", "form", "nameField", "subjectField", "subjectHintIcon", "sendTestButton", "trixEditor",
      "saveLoader", "emailIcon", "saveMessage", "errorMessageIcon", "errorMessageContainer", "templateSelect",
      "waitDaysSelect", "bodyContent", "spamButton", "editedTag"]
  }

  static get values() {
    return {
      id: Number,
      justSaved: Boolean,
      method: String
    }
  }

  connect() {
    this.debouncedSave = DebounceHelper.debounce(this.save.bind(this), 1000)
    this.hasChanges = false
    this.initialSubject = this.subjectFieldTarget.value
    this.initialBody = this.trixEditorTarget.editor.getDocument().toString()

    this.setupAutoSave()
    this.setupBlurSave()
  }

  disconnect() {
    if (this.debouncedSave) {
      this.debouncedSave.cancel()
    }

    // Cleanup attachment event listeners to prevent memory leaks
    if (this.attachmentEventCleanup) {
      this.attachmentEventCleanup.cleanup()
    }
  }

    setupAutoSave() {
    // Set flag to ensure user interaction before auto-save
    this.userInteracted = false

    // Setup attachment and user interaction events using shared helper
    this.attachmentEventCleanup = AttachmentEventHelper.setupAllEvents(
      this.trixEditorTarget,
      () => {
        this.userInteracted = true
      },
      () => {
        // Trigger save for attachment changes (since they might not fire trix-change)
        this.hasChanges = true
        this.debouncedSave()
      }
    )

    // Listen for changes in the editor (only after user interaction)
    this.trixEditorTarget.addEventListener("trix-change", () => {
      if (!this.userInteracted) return
      this.hasChanges = true
      this.debouncedSave()
    })

    // Listen for changes in the subject field
    this.subjectFieldTarget.addEventListener("input", () => {
      this.hasChanges = true
      this.debouncedSave()
    })
  }

  setupBlurSave() {
    // Save on blur for subject field if there were changes
    this.subjectFieldTarget.addEventListener("blur", () => {
      if (this.hasChanges && this.subjectFieldTarget.value !== this.initialSubject) {
        if (this.debouncedSave) {
          this.debouncedSave.cancel()
        }

        this.save()
      }
    })

    // Save on blur for trix editor if there were changes
    this.trixEditorTarget.addEventListener("blur", () => {
      if (this.hasChanges && this.trixEditorTarget.editor.getDocument().toString() !== this.initialBody) {
        if (this.debouncedSave) {
          this.debouncedSave.cancel()
        }

        this.save()
      }
    })
  }

  save() {
    if (!this.formTarget || this.justSavedValue) return

    const formData = new FormData(this.formTarget)
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

    this.showSaveLoader()

    fetch(this.formTarget.action, {
      method: this.methodValue || "POST",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "application/json"
      },
      body: formData,
      signal: controller.signal
    })
      .then(async response => {
        clearTimeout(timeoutId)

        let errorMessage = i18n.t("js.back.campaigns.message.errors.save_failed")

        if (!response.ok) {
          try {
            const errorData = await response.json()
            errorMessage = errorData.message || errorData.error || errorMessage
          } catch {
            errorMessage = this.getErrorMessage(response.status)
          }

          throw new Error(errorMessage)
        }

        return response.json()
      })
      .then(response => {
        this.showSaveMessage()
        this.showEditedTag()
        this.justSavedValue = true
        this.hasChanges = false

        // For message in text format we need to ensure we use the stripped version
        if (response.message.message_format == "text") {
          this.trixEditorTarget.editor.loadHTML(response.message.body)
        }

        this.initialSubject = this.subjectFieldTarget.value
        this.initialBody = this.trixEditorTarget.editor.getDocument().toString()

        // If this was the initial create, switch the form to update mode
        if ((this.methodValue?.toUpperCase() || "POST") === "POST" && response.message?.id) {
          const createdId = response.message.id
          if (createdId) {
            this.idValue = createdId
            this.methodValue = "PUT"
            this.formTarget.action = `/messages/${createdId}`
          }
        }

        setTimeout(() => {
          this.justSavedValue = false
        }, 1000)
      })
      .catch(error => {
        clearTimeout(timeoutId)

        if (error.name === 'AbortError') {
          this.showErrorMessage(i18n.t("js.back.campaigns.message.errors.timeout"))
        } else if (error.name === 'SyntaxError') {
          this.showErrorMessage(i18n.t("js.back.campaigns.message.errors.save_failed"))
        } else {
          this.showErrorMessage(error.message || i18n.t("js.back.campaigns.message.errors.unexpected_error"))
        }
      })
      .finally(() => {
        this.hideSaveLoader()
      })
  }

  showSaveLoader() {
    this.saveLoaderTarget.classList.remove("d-none")
    this.emailIconTarget.classList.add("d-none")
  }

  hideSaveLoader() {
    this.saveLoaderTarget.classList.add("d-none")
  }

  showSaveMessage() {
    this.emailIconTarget.classList.add("d-none")
    this.saveMessageTarget.classList.remove("d-none")
    setTimeout(() => {
      this.saveMessageTarget.classList.add("d-none")
      this.emailIconTarget.classList.remove("d-none")
    }, 2000)
  }

  showEditedTag() {
    if (this.editedTagTarget) {
      this.editedTagTarget.classList.remove("d-none")
    }
  }

  showErrorMessage(message) {
    this.emailIconTarget.classList.add("d-none")
    this.errorMessageIconTarget.classList.remove("d-none")
    this.errorMessageContainerTarget.textContent = message
    this.errorMessageContainerTarget.classList.remove("d-none")
    setTimeout(() => {
      this.errorMessageIconTarget.classList.add("d-none")
      this.errorMessageContainerTarget.classList.add("d-none")
      this.emailIconTarget.classList.remove("d-none")
    }, 3000)
  }

  // Handle template selection
  templateSelected(event) {
    const template = event.target.selectedOptions[0]
    if (!template.value) return

    const subject = template.dataset.subject
    const body = template.dataset.body
    const messageFormat = template.dataset.messageFormat

    if (subject) {
      this.subjectFieldTarget.value = subject
    }

    if (body && this.hasTrixEditorTarget) {
      this.trixEditorTarget.editor.loadHTML(body)
    }

    // Apply the message_format from the template
    if (messageFormat) {
      const messageFormatInput = this.formTarget.querySelector("input[name='message[message_format]']")
      if (messageFormatInput) {
        messageFormatInput.value = messageFormat
      }

      // Toggle HTML mode in trix-editor based on message_format
      const trixEditorElement = this.element.querySelector('[data-controller~="trix-editor"]')
      if (trixEditorElement) {
        const trixEditorController = this.application.getControllerForElementAndIdentifier(
          trixEditorElement,
          "trix-editor"
        )

        if (trixEditorController) {
          if (messageFormat === "html") {
            trixEditorController.enableHtml()
          } else if (messageFormat === "text") {
            trixEditorController.disableHtml(true)
          }
        }
      }
    }

    this.debouncedSave()
  }

  getErrorMessage(status) {
    switch (status) {
      case 422:
        return i18n.t("js.back.campaigns.message.errors.validation_error")
      case 401:
        return i18n.t("js.back.campaigns.message.errors.session_expired")
      case 403:
        return i18n.t("js.back.campaigns.message.errors.permission_denied")
      case 500:
        return i18n.t("js.back.campaigns.message.errors.server_error")
      default:
        return i18n.t("js.back.campaigns.message.errors.save_failed")
    }
  }
}
